import { useTranslation } from "react-i18next";
import type { TimeAttendanceStepProps } from "@/types/timeAttendance";
import {
  CircleCheck,
  CircleX,
  Hourglass,
  ListChecks,
  ScanFace,
} from "lucide-react";
import {
  BiometricImg,
  DesktopImg,
  LocationImg,
  MobileImg,
} from "@/assets/images";
import Countdown from "react-countdown";
import { CheckInMethodEnum } from "@/utils/enums";
import { startCase } from "lodash";
import { ENUM_ATTENDANCE_REQUEST_TYPE } from "@/utils/enums/time-attendance.enum";

const attendanceRules = [
  {
    key: "isAllowWebCheckinCheckout",
    labelKey: "my-space:ui.check_ins_and_check_outs_from_web",
    img: DesktopImg,
  },
  {
    key: "isAllowMobileCheckinCheckout",
    labelKey: "my-space:ui.check_ins_and_check_outs_from_mobile",
    img: MobileImg,
  },
  {
    key: "isLocationMandatoryCheckinCheckout",
    labelKey:
      "my-space:ui.location_sharing_mandatory_during_check_in_and_check_out",
    img: LocationImg,
  },
  {
    key: "isCapturePhotoCheckinCheckout",
    labelKey:
      "my-space:ui.capture_and_verify_photo_during_check_in_and_check_out",
    img: BiometricImg,
  },
  {
    key: "isCameraVerified",
    labelKey: "my-space:checkin.recognized_by_face_camera",
    img: BiometricImg,
  },
] as const;

const TimeAttendanceRuleStep1: React.FC<TimeAttendanceStepProps> = ({
  onNext,
  onReject,
  eligibility,
}) => {
  const { t } = useTranslation(["common", "my-space"]);
  const requestTypeLabel =
    eligibility?.requestType === ENUM_ATTENDANCE_REQUEST_TYPE.CHECK_OUT
      ? t("my-space:time_attendance.check_out")
      : eligibility?.requestType === ENUM_ATTENDANCE_REQUEST_TYPE.CHECK_IN
        ? t("my-space:time_attendance.check_in")
        : startCase(eligibility?.requestType)?.replace(" ", "-");

  const ruleSummary = eligibility?.timeAttendanceRuleSummary ?? {};

  const visibleRules = attendanceRules.filter(
    (rule) => rule.key in ruleSummary,
  );

  const renderer = ({
    minutes,
    seconds,
    completed,
  }: {
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) return <span>{t("my-space:checkin.expired")}</span>;
    return (
      <span>
        {t("my-space:checkin.expires_in", {
          minutes: minutes.toString().padStart(2, "0"),
          seconds: seconds.toString().padStart(2, "0"),
        })}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Icon */}
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center bg-blue-400/10">
          <ListChecks size={20} className="text-info w-7 h-7" />
        </div>
      </div>

      {/* Heading */}
      <h3 className="text-lg font-semibold text-center">
        {t("my-space:checkin.almost_there")}
      </h3>

      <p className="text-sm text-black/50 text-center mt-1">
        {t("my-space:checkin.complete_steps_to_finish_checkin")}
      </p>

      {/* Timer */}
      <div className="flex justify-center items-center mt-4 mb-6">
        <p className="flex justify-center items-center px-4 py-1.5 rounded-full bg-amber-200 text-amber-600 text-xs font-semibold">
          <Hourglass size={18} className="inline me-1" />{" "}
          <Countdown
            date={new Date(eligibility.expiredAt)}
            renderer={renderer}
          />
        </p>
      </div>

      {/* Rules Label */}
      {Object.keys(eligibility?.timeAttendanceRuleSummary).length > 0 && (
        <div className="flex flex-col gap-2">
          <h6 className="uppercase text-sm font-normal text-black/50 flex items-center gap-2">
            {t("my-space:checkin.rules_compliance_summary_colon")}
          </h6>
          <div className="flex flex-col gap-2 border rounded-xs">
            {visibleRules.map((rule, idx) => {
              const value = ruleSummary[rule.key];

              return (
                <div
                  key={rule.key}
                  className={`flex justify-between items-center ${
                    idx === visibleRules.length - 1 ? "" : "border-b"
                  } p-1 flex-wrap sm:flex-nowrap gap-2`}
                >
                  <div className="flex items-center gap-2">
                    <img
                      width={40}
                      height={40}
                      src={rule.img}
                      alt={t(rule.labelKey)}
                      className="shrink-0"
                    />

                    <span className="text-sm">{t(rule.labelKey)}</span>
                  </div>

                  <div className="shrink-0">
                    {value ? (
                      <CircleCheck size={18} className="text-green-500" />
                    ) : (
                      <CircleX size={18} className="text-red-500" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No face camera recognized the employee within the rule's window.
          Standing in front of one and checking in again is usually quicker
          than HR approval, so say so before they send the request. */}
      {ruleSummary.isCameraVerified === false && (
        <div className="flex gap-2 items-start mt-4 rounded-xs border border-warning/40 bg-warning-muted px-3 py-2.5">
          <ScanFace size={18} className="text-warning shrink-0 mt-0.5" />
          <p className="text-xs text-warning font-medium">
            {t("my-space:checkin.not_recognized_by_face_camera_hint")}
          </p>
        </div>
      )}

      {eligibility?.empAttendanceRulesSummary?.method && (
        <div className="flex flex-col gap-2 mt-4">
          <h6 className="uppercase text-sm font-normal text-black/50 flex items-center gap-2">
            {t("my-space:checkin.employee_rules_compliance_summary_colon")}
          </h6>
          <div className="flex flex-col gap-2 border rounded-xs">
            {/* Web Check-ins */}
            <div className="flex justify-between items-center  p-2">
              <div className="flex items-center ">
                <img
                  width={40}
                  height={40}
                  alt={t("my-space:checkin.web")}
                  src={LocationImg}
                  className="shrink-0"
                />
                <span className="text-sm">
                  {eligibility?.empAttendanceRulesSummary?.method ===
                  CheckInMethodEnum.SpecificLocation
                    ? t("common:request_detail.checkin_permitted_at_location")
                    : t("common:request_detail.checkin_allowed_within_radius")}
                </span>
              </div>
              {eligibility?.empAttendanceRulesSummary
                ?.isAllowEmployeeTimeAttendanceRule ? (
                <CircleCheck size={18} className="text-green-500" />
              ) : (
                <CircleX size={18} className="text-red-500" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="mt-6 space-y-2">
        <button
          onClick={onNext}
          className="w-full h-11 rounded-xs font-semibold transition bg-primary text-white hover:opacity-90"
        >
          {t("my-space:checkin.complete_required_steps")}
        </button>

        <button
          onClick={() => onReject?.("User cancelled")}
          className="w-full h-11 rounded-xs border bg-muted hover:bg-muted/70 transition"
        >
          {t("my-space:checkin.cancel_type", { type: requestTypeLabel })}
        </button>
      </div>
    </div>
  );
};

export default TimeAttendanceRuleStep1;
